import { useEffect, useState } from 'react';
import { useFileDownload } from 'hooks/useFileDownload';
import { useFetch } from 'hooks/use-fetch/use-fetch';
import { exportsRequestConfig } from 'request-config/exports/exports.request-config';
import { useQueryClient } from 'react-query';
import { ReactQueryKeys } from 'common/enums';
import useOverlay from 'hooks/use-overlay';

const generatingStatus = {
  pending: 0,
  generated: 1,
  failed: 2
};

export const useHandleGenerating = () => {
  const [count, setCount] = useState(0);
  const [runInterval, setRunInterval] = useState(false);
  const [stopGenerating, setStopGenerating] = useState(false);
  const [showSnackbar, setShowSnackBar] = useState(false);
  const [recordId, setRecordId] = useState('');
  const [fileName, setFileName] = useState('');
  const queryClient = useQueryClient();
  const [showOverlay, hideOverlay] = useOverlay();

  const clearStates = () => {
    setCount(0);
    setRunInterval(false);
    setRecordId('');
    setFileName('');
    setStopGenerating(false);
    hideOverlay();
  };

  const downloadFile = useFileDownload({
    fileName,
    encodeConfig: {
      path: [],
      encodeWay: 'newBlob'
    }
  });

  const { fetch: getDocumentState } = useFetch({
    loadingKey: 'getDocumentState',
    isShowLoading: false
  });

  const handleDocumentState = async (recordId: string) => {
    try {
      const response = await getDocumentState({
        requestConfig: exportsRequestConfig.getDocumentState(recordId)
      });

      if (response) {
        const { data } = response.data;

        if (data.recordState === generatingStatus.generated) {
          await downloadFile({
            requestConfig: exportsRequestConfig.downloadFile(recordId),
            fileName
          });

          queryClient.refetchQueries([ReactQueryKeys.DownloadManageCounter]);
          setStopGenerating(true);
          return;
        }

        if (data.recordState === generatingStatus.failed) {
          setStopGenerating(true);
          return;
        }

        if (count === 2) {
          setShowSnackBar(true);
        }

        setCount(prev => prev + 1);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleGenerating = async (recordId: string, isManualMode: boolean, fileName: string) => {
    try {
      if (isManualMode) {
        await downloadFile({
          requestConfig: exportsRequestConfig.downloadFile(recordId),
          fileName
        });
      } else {
        setFileName(fileName);
        setRecordId(recordId);
        setRunInterval(true);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (runInterval && recordId) {
      showOverlay();
      setShowSnackBar(false);
      handleDocumentState(recordId);
    }
  }, [runInterval, recordId]);

  useEffect(() => {
    if (runInterval && recordId) {
      const interval = setInterval(() => handleDocumentState(recordId), 10000);

      if (count === 3 || stopGenerating) {
        clearInterval(interval);
        clearStates();
      }

      return () => {
        clearInterval(interval);
      };
    }
  }, [runInterval, recordId, count, stopGenerating]);

  useEffect(() => {
    return hideOverlay;
  }, []);

  return {
    showSnackbar,
    handleGenerating
  };
};
